import { Box, BoxContent, Chart, Heading } from '@mm/components';
import { ArrowCircleStatefulIcon } from '@mm/icons';
import { useAnalyticsCRUDActions } from '@mm/state/actions/AnalyticsCRUDActions';
import { Trend } from '@mm/state/model/ConnectedPageModel';
import { selectAnalytics } from '@mm/state/selectors/analyticsSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useFacebookErrorCRUDActions } from '@mm/state/actions/FacebookErrorCRUDActions';
import { abbreviateNumber } from '@mm/utils';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { GreenMarker } from '../../../../../common/components/GreenMarker/GreenMarker';
import { useGreenCueMarkers } from '../../../../../common/hooks/use-green-cue-markers';
import { UserRoleContainer } from '../../../../../common/hooks/use-route-guard';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { colors } from '../../styles/colors';
import { getDormatedDateLabel } from '../../utils/dateUtils';
import { Channels } from './Channels/Channels';
import './Dashboard.pcss';
import { GuidesForNewUsers } from './GuidesForNewUsers/GuidesForNewUsers';
import { VerificationForm } from '../../../../../modules/LoginRouter/components/VerificationForm/VerificationForm';
import { FacebookPageErrors } from '../../../../../common/components/FacebookPageAlerts/FacebookPageErrors';
import { NewDashboard } from './AutomationsDashboard/AutomationsDashboard';
import { useSubscribeFromTrialPromoModal } from '../../../../../use-subscribe-from-trial-promo-modal';
import { useFailedPaymentModal } from '../../../../../common/modals/FailedPaymentModal/FailedPaymentModal';

type Props = {
  className?: string;
};

const DiffBlock = ({
  direction,
  value,
  period,
  showDiffBlock,
}: {
  direction: Trend;
  value: number;
  period: string;
  showDiffBlock: boolean;
}) => {
  const isUp = direction === Trend.Up;
  const isDown = direction === Trend.Down;
  const isSame = direction === Trend.Same;

  if (showDiffBlock && (isSame || isDown)) {
    return <div className="dashboard__weekly-change_same_or_down" />;
  }
  if (!showDiffBlock) return <div className="dashboard__weekly-change_empty" />;

  const colorsByTrend = {
    [Trend.Up]: colors.green,
    [Trend.Down]: colors.red,
    [Trend.Same]: colors.grayLight,
  };
  const color = colorsByTrend[direction];

  return (
    <div className="dashboard__weekly-change">
      <div className="dashboard__weekly-change-arrow">
        <ArrowCircleStatefulIcon
          size={14}
          color={color}
          direction={isUp ? 'up' : isDown ? 'down' : undefined}
        />
      </div>
      <span style={{ color, marginRight: 3 }}>
        <span>
          {isUp && '+'}
          {isDown && '-'}
        </span>
        <span>
          {Math.abs(value)}
          {'%'}{' '}
        </span>
      </span>
      <span>
        {' '}
        {isUp && 'more'}
        {isDown && 'less'} than last {period}
      </span>
    </div>
  );
};

const DashboardMain = ({ className }: Props) => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  // chart
  const chatbotId = useSelector(activeChatbotIdSelector);

  const analyticsCRUDActions = useAnalyticsCRUDActions();

  const analytics = useSelector(selectAnalytics);

  const { isFreeTrialPromotionWithoutSubscription } =
    useFreeTrialUpgradeModal();

  const showActivationCues = useSelector(
    combinedFeatureFlagsSelector,
  ).show_cues;

  const { numberOfCuesLeft } = useGreenCueMarkers();
  const DAY = 'day';
  const { history } = useRouter();
  const facebookErrorCRUDActions = useFacebookErrorCRUDActions();

  useEffect(() => {
    history.push(BotBuilderRoutes.dashboard.getLink({ chatbotId }));
    if (chatbotId) {
      const startDate = moment().subtract(30, 'days');
      const endDate = moment();
      const granularity = DAY;
      analyticsCRUDActions.retrieveAll(
        Number(chatbotId),
        startDate,
        endDate,
        granularity,
      );
    }
  }, [chatbotId]);

  useEffect(() => {
    if (activeFacebookPage) {
      facebookErrorCRUDActions.retrieveAll(activeFacebookPage.id);
    }
  }, [activeFacebookPage?.id]);

  const totalContacts = activeFacebookPage?.totalContacts;
  const contactsThisWeek = activeFacebookPage?.newContactsThisWeek;
  const sessionsThisWeek = activeFacebookPage?.newSessionsThisWeek;

  const totalContactsData = analytics?.totalContacts && {
    labels: getDormatedDateLabel(analytics?.date, DAY),
    data: [analytics.totalContacts],
    color: [colors.accentBlue],
    title: ['Total'],
  };

  const showDiffBlock = Boolean(
    [
      activeFacebookPage?.sessionsTrend ||
        activeFacebookPage?.contactsTrend ||
        activeFacebookPage?.totalContactsTrend,
    ].find(trend => trend === 'up'),
  );

  return (
    <div className={classNames(className, 'dashboard')}>
      <div className="dashboard_top-block">
        <FacebookPageErrors />
        <Box className="dashboard__top-stats">
          <Heading text="This Week" size={3} />
          <div className="dashboard__top-stats-row">
            <BoxContent className="dashboard__info-block">
              <Heading text="Sessions" size={4} />
              <span className="dashboard__top-stat">{sessionsThisWeek}</span>
              {activeFacebookPage && (
                <DiffBlock
                  direction={activeFacebookPage?.sessionsTrend}
                  value={activeFacebookPage?.sessionsTrendPercentage}
                  period="week"
                  showDiffBlock={showDiffBlock || false}
                />
              )}
            </BoxContent>

            <BoxContent className="dashboard__info-block">
              <Heading text="Contacts" size={4} />
              <span className="dashboard__top-stat">{contactsThisWeek}</span>
              {activeFacebookPage && (
                <DiffBlock
                  direction={activeFacebookPage?.contactsTrend}
                  value={activeFacebookPage?.contactsTrendPercentage}
                  period="week"
                  showDiffBlock={showDiffBlock || false}
                />
              )}
            </BoxContent>

            <BoxContent className="dashboard__info-block">
              <Heading text="Total Contacts" size={4} />
              <span className="dashboard__top-stat">
                {abbreviateNumber(Number(totalContacts))}
              </span>
              {activeFacebookPage && (
                <DiffBlock
                  direction={activeFacebookPage?.totalContactsTrend}
                  value={activeFacebookPage?.totalContactsTrendPercentage}
                  period="week"
                  showDiffBlock={showDiffBlock || false}
                />
              )}
            </BoxContent>
            <Channels />
          </div>
          {showActivationCues &&
            isFreeTrialPromotionWithoutSubscription &&
            numberOfCuesLeft > 0 && (
              <div className="dashboard__markers-block">
                <GreenMarker />
                <div className="dashboard__markers-block--text">
                  <div>
                    Follow the{' '}
                    <span className="dashboard__markers-block--text-green">
                      green markers
                    </span>{' '}
                    to explore Customers.ai's Chat Marketing features and add
                    days to your free trial!
                  </div>
                  <div className="dashboard__markers-block--text-bold">
                    There are {numberOfCuesLeft} free days left to be found!
                  </div>
                </div>
              </div>
            )}
        </Box>
      </div>

      <GuidesForNewUsers className="dashboard__guides" />

      <div className="dashboard__charts">
        <Chart
          className="dashboard__chart-main"
          name="Total contacts"
          data={totalContactsData}
        />
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const { needsToVerifyEmail } = useSelector(selectSessionSelector);

  const {
    isLoading,
    roleFlags: { isLimitedRole },
  } = UserRoleContainer.useContainer();

  if (isLoading || isLimitedRole) {
    return <></>;
  }

  if (needsToVerifyEmail) {
    return <VerificationForm />;
  }

  return <DashboardMain />;
};

export const AutomationsDashboard = () => {
  useSubscribeFromTrialPromoModal();
  useFailedPaymentModal();

  const {
    isLoading,
    roleFlags: { isLimitedRole },
  } = UserRoleContainer.useContainer();

  if (isLoading || isLimitedRole) {
    return <></>;
  }

  return <NewDashboard />;
};
